import { Order, OrderStatus } from '@/types'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { statusColors } from '../common/tables/columns'
import { useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/20/solid'

interface LiveOrderTrackingProps {
  orders: Order[]
}

const formatDate = (date: Date) => format(date, 'dd.MM.yyyy', { locale: de })

const ACTIVE_STATUSES = [
  'rfq',
  'quoted',
  'in_draft',
  'pending_confirmation',
  'confirmed',
  'in_production',
  'final_testing',
  'ready_for_shipping',
  'in_delivery'
] as const

const STATUS_STEPS = [
  { status: 'rfq', label: 'RFQ' },
  { status: 'quoted', label: 'Quoted' },
  { status: 'in_draft', label: 'In Draft' },
  { status: 'pending_confirmation', label: 'Pending Confirmation' },
  { status: 'confirmed', label: 'Confirmed' },
  { status: 'in_production', label: 'In Production' },
  { status: 'final_testing', label: 'Final Testing' },
  { status: 'ready_for_shipping', label: 'Ready for Shipping' },
  { status: 'in_delivery', label: 'In Delivery' },
  { status: 'completed', label: 'Completed' }
] as const

const getStepProgress = (order: Order): { currentStep: number; totalSteps: number; subProgress: number } => {
  const currentStepIndex = STATUS_STEPS.findIndex(step => step.status === order.status)
  const totalSteps = STATUS_STEPS.length
  
  if (currentStepIndex === -1) return { currentStep: 0, totalSteps: 0, subProgress: 0 }
  
  let subProgress = 0
  
  // Calculate sub-progress for specific statuses
  switch (order.status) {
    case 'in_production':
      subProgress = order.productionProgress || 0
      break
    case 'final_testing':
      subProgress = ((order.qualityChecksPassed || 0) / (order.qualityChecksTotal || 5)) * 100
      break
    case 'in_delivery':
      subProgress = 50 // Halfway through delivery
      break
    default:
      subProgress = 100 // Full progress for other statuses
  }
  
  return {
    currentStep: currentStepIndex + 1,
    totalSteps,
    subProgress
  }
}

export function LiveOrderTracking({ orders }: LiveOrderTrackingProps) {
  const [pageSize, setPageSize] = useState(5)
  const [currentPage, setCurrentPage] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatuses, setSelectedStatuses] = useState<typeof ACTIVE_STATUSES[number][]>([])

  // Filter orders by active status and search query
  const filteredOrders = orders
    .filter(order => ACTIVE_STATUSES.includes(order.status as typeof ACTIVE_STATUSES[number]))
    .filter(order => {
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(order.status as typeof ACTIVE_STATUSES[number])) {
        return false
      }
      
      if (!searchQuery) return true
      
      const searchLower = searchQuery.toLowerCase()
      return (
        order.orderNumber.toLowerCase().includes(searchLower) ||
        order.products.some(p => p.name.toLowerCase().includes(searchLower)) ||
        order.status.toLowerCase().includes(searchLower)
      )
    })
    .map(order => ({
      ...order,
      progress: getStepProgress(order)
    }))
    .sort((a, b) => {
      // Sort by progress first (descending)
      const progressDiff = b.progress.currentStep - a.progress.currentStep
      if (progressDiff !== 0) return progressDiff

      // Then by revised delivery date (ascending)
      const aDate = a.revisedDeliveryDate || a.orderDate
      const bDate = b.revisedDeliveryDate || b.orderDate
      return aDate.getTime() - bDate.getTime()
    })

  const pageCount = Math.ceil(filteredOrders.length / pageSize)
  const displayOrders = filteredOrders.slice(currentPage * pageSize, (currentPage + 1) * pageSize)

  if (orders.filter(order => ACTIVE_STATUSES.includes(order.status as typeof ACTIVE_STATUSES[number])).length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Live Order Tracking
        </h2>
        <p className="text-gray-500 dark:text-gray-400">No active orders at the moment.</p>
      </div>
    )
  }

  const renderStatus = (status: OrderStatus) => {
    const color = statusColors[status];
    return (
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 rounded-full text-xs ${color.selected}`}>
          {status.replace(/_/g, ' ')}
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Live Order Tracking
              </h2>
              <span className="text-sm text-gray-500 dark:text-gray-400 sm:hidden">
                {filteredOrders.length} active orders
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="hidden sm:block text-sm text-gray-500 dark:text-gray-400">
                {filteredOrders.length} active orders
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search orders..."
                className="w-full sm:w-auto px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {ACTIVE_STATUSES.map(status => {
              const isSelected = selectedStatuses.includes(status)
              const colorClass = isSelected ? statusColors[status].selected : statusColors[status].unselected
              const statusCount = orders.filter(order => order.status === status).length

              return (
                <button
                  key={status}
                  onClick={() => setSelectedStatuses(prev => {
                    const newStatuses = isSelected
                      ? prev.filter(s => s !== status as typeof ACTIVE_STATUSES[number])
                      : [...prev, status as typeof ACTIVE_STATUSES[number]]
                    return newStatuses
                  })}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${colorClass}`}
                >
                  <span>{status.replace(/_/g, ' ')}</span>
                  <span className="text-xs">({statusCount})</span>
                  {isSelected && (
                    <span className="font-medium">&times;</span>
                  )}
                </button>
              )
            })}
            {selectedStatuses.length >= 2 && (
              <button
                onClick={() => setSelectedStatuses([])}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                deselect all
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Order
              </th>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Progress
              </th>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Date Ordered
              </th>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Requested Delivery
              </th>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Confirmed Delivery
              </th>
              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Revised Delivery
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displayOrders.map(order => {
              return (
                <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-3 sm:px-6 py-2 sm:py-4">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {order.orderNumber}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {order.products.length} details
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4">
                    {renderStatus(order.status as OrderStatus)}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-1 mb-1">
                          {STATUS_STEPS.map((step, index) => {
                            const isPast = index < STATUS_STEPS.findIndex(s => s.status === order.status)
                            const isCurrent = step.status === order.status
                            const { subProgress } = getStepProgress(order)
                            
                            return (
                              <div
                                key={step.status}
                                className={`
                                  flex-1 h-1.5 rounded-full
                                  ${isPast ? 'bg-green-500' : isCurrent ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}
                                `}
                              >
                                {isCurrent && (
                                  <div
                                    className="h-1.5 rounded-full bg-blue-600"
                                    style={{ width: `${subProgress}%` }}
                                  />
                                )}
                              </div>
                            )
                          })}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Step {getStepProgress(order).currentStep} of {getStepProgress(order).totalSteps}: {STATUS_STEPS.find(s => s.status === order.status)?.label}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {formatDate(order.orderDate)}
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {order.requestedDeliveryDate ? formatDate(order.requestedDeliveryDate) : '-'}
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {order.confirmedDeliveryDate ? formatDate(order.confirmedDeliveryDate) : '-'}
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {order.revisedDeliveryDate ? formatDate(order.revisedDeliveryDate) : '-'}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className="p-1 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            >
              <ChevronDoubleLeftIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentPage(p => p - 1)}
              disabled={currentPage === 0}
              className="p-1 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={currentPage >= pageCount - 1}
              className="p-1 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentPage(pageCount - 1)}
              disabled={currentPage >= pageCount - 1}
              className="p-1 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
            >
              <ChevronDoubleRightIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
              <div>Page</div>
              <strong>
                {currentPage + 1} of {pageCount}
              </strong>
            </span>
            <select
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value))
                setCurrentPage(0) // Reset to first page when changing page size
              }}
              className="px-2 py-1 border rounded bg-white dark:bg-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm"
            >
              {[5, 10, 15, 20].map(size => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  )
} 